import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ScrollText, Activity, AlertTriangle, Search, RefreshCw, Loader2,
  Filter, User, Database, Sparkles, FileText, Shield, Server, Clock, ChevronDown
} from 'lucide-react';

const ACTION_COLORS = {
  login: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  google_auth: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  execute_query: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  ai_chat: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  create_report: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  update_report: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  delete_report: 'bg-red-500/15 text-red-400 border-red-500/20',
  create_visual: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
  update_tenant_config: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  error: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const ACTION_ICONS = {
  login: User, google_auth: User, execute_query: Database,
  ai_chat: Sparkles, create_report: FileText, update_report: FileText,
  delete_report: FileText, create_visual: FileText, update_tenant_config: Shield,
};

export default function LoggingPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('activity');
  const [activityLogs, setActivityLogs] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [logLimit, setLogLimit] = useState(100);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [activity, system] = await Promise.all([
        api.getActivityLogs({ limit: logLimit, action: filterAction !== 'all' ? filterAction : undefined }).catch(() => []),
        api.getSystemLogs({ limit: logLimit, severity: filterSeverity !== 'all' ? filterSeverity : undefined }).catch(() => []),
      ]);
      setActivityLogs(activity || []);
      setSystemLogs(system || []);
    } catch {}
    finally { setLoading(false); }
  }, [logLimit, filterAction, filterSeverity]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadLogs();
    setRefreshing(false);
  };

  const filteredActivity = activityLogs.filter(log =>
    !searchQuery || JSON.stringify(log).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSystem = systemLogs.filter(log =>
    !searchQuery || JSON.stringify(log).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const actionTypes = [...new Set(activityLogs.map(l => l.action))].sort();

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return <div className="p-6 text-center"><p className="text-muted-foreground">Admin access required</p></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto pb-24 md:pb-6" data-testid="logging-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold flex items-center gap-2">
            <ScrollText className="w-5 h-5 md:w-6 md:h-6 text-primary" /> System Logs
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">Activity tracking and system diagnostics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Select value={String(logLimit)} onValueChange={v => setLogLimit(Number(v))}>
            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50 logs</SelectItem>
              <SelectItem value="100">100 logs</SelectItem>
              <SelectItem value="500">500 logs</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-0.5 rounded-lg bg-muted/30 w-fit">
        <button onClick={() => setActiveTab('activity')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'activity' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <Activity className="w-3.5 h-3.5" /> Activity Log
          <Badge variant="secondary" className="text-[8px] h-4 px-1">{activityLogs.length}</Badge>
        </button>
        <button onClick={() => setActiveTab('system')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'system' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <Server className="w-3.5 h-3.5" /> System Log
          <Badge variant="secondary" className="text-[8px] h-4 px-1">{systemLogs.length}</Badge>
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search logs..." className="pl-9 h-8 text-xs" />
        </div>
        {activeTab === 'activity' && (
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-40 h-8 text-xs"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actionTypes.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {activeTab === 'system' && (
          <Select value={filterSeverity} onValueChange={setFilterSeverity}>
            <SelectTrigger className="w-36 h-8 text-xs"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="debug">Debug</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : activeTab === 'activity' ? (
        /* Activity Log */
        <div className="space-y-1">
          {filteredActivity.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <Activity className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No activity logs found</p>
            </div>
          ) : filteredActivity.map((log, i) => {
            const Icon = ACTION_ICONS[log.action] || Activity;
            const color = ACTION_COLORS[log.action] || 'bg-muted/20 text-muted-foreground border-border/20';
            return (
              <div key={log.log_id || i} className="flex items-start gap-3 p-3 rounded-lg border border-border/20 hover:border-border/40 transition-colors group" data-testid={`activity-log-${i}`}>
                <div className={`p-1.5 rounded-lg border ${color} flex-shrink-0`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[8px] ${color}`}>{(log.action || '').replace(/_/g, ' ')}</Badge>
                    <span className="text-[10px] text-muted-foreground">{log.user_name || log.user_id}</span>
                    {log.resource_type && <span className="text-[9px] text-muted-foreground/70">{log.resource_type}</span>}
                  </div>
                  {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5 truncate max-w-lg font-mono">
                      {JSON.stringify(log.details).slice(0, 120)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[9px] text-muted-foreground/70 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* System Log */
        <div className="space-y-1">
          {filteredSystem.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <Server className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No system logs found</p>
              <p className="text-xs text-muted-foreground/70 mt-1">System logs track errors, warnings, and backend events</p>
            </div>
          ) : filteredSystem.map((log, i) => {
            const sevColors = { error: 'text-red-400 bg-red-500/10 border-red-500/20', warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20', info: 'text-blue-400 bg-blue-500/10 border-blue-500/20', debug: 'text-muted-foreground bg-muted/20 border-border/20' };
            const sev = log.severity || 'info';
            return (
              <div key={log.log_id || i} className={`p-3 rounded-lg border ${sevColors[sev]?.split(' ').slice(2).join(' ') || 'border-border/20'} hover:border-border/40 transition-colors font-mono text-[11px]`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-[8px] ${sevColors[sev] || ''}`}>{sev.toUpperCase()}</Badge>
                  <span className="text-muted-foreground/70">{log.source || 'system'}</span>
                  <span className="text-muted-foreground/30 ml-auto flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </span>
                </div>
                <p className={`${sevColors[sev]?.split(' ')[0] || 'text-foreground'}`}>{log.message}</p>
                {log.stack_trace && (
                  <details className="mt-1">
                    <summary className="text-[9px] text-muted-foreground/70 cursor-pointer hover:text-muted-foreground">Stack trace</summary>
                    <pre className="text-[9px] text-muted-foreground/70 mt-1 whitespace-pre-wrap max-h-32 overflow-auto">{log.stack_trace}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
